import React from 'react';
import {
    List,
    Datagrid,
    DateField,
    BooleanField,
    SearchInput,
    SelectInput,
    FilterButton,
    CreateButton,
    ExportButton,
    TopToolbar,
    useTranslate,
    FunctionField,
    EditButton,
    ShowButton,
    DeleteButton,
    BulkDeleteButton,
    BulkExportButton,
    useListContext,
    RecordContextProvider,
    RefreshButton,
} from 'react-admin';
import {
    Box,
    Chip,
    Typography,
    Tooltip,
    Fab,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    AttachMoney,
    CheckCircle,
    Cancel,
    AdminPanelSettings,
    PersonOutline,
    Add as AddIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { FeeTemplate } from '../../types/fee.types';

const DesktopListActions = () => (
    <TopToolbar>
        <FilterButton />
        <CreateButton />
        <ExportButton />
    </TopToolbar>
);

const MobileListActions = () => (
    <Box sx={{ display: 'flex', justifyContent: 'flex-end', p: 0 }}>
        <ExportButton />
    </Box>
);

const useFeeTemplateFilters = () => {
    const translate = useTranslate();

    return [
        <SearchInput key="search" source="search" placeholder={translate('resources.fee-templates.list.filters.search')} alwaysOn />,
        <SelectInput
            key="isActive"
            source="isActive"
            label={translate('resources.fee-templates.fields.isActive')}
            choices={[
                { id: true, name: translate('resources.fee-templates.list.filters.active') },
                { id: false, name: translate('resources.fee-templates.list.filters.inactive') },
            ]}
            emptyText={translate('resources.fee-templates.list.filters.all')}
            emptyValue=""
        />,
        <SelectInput
            key="isSystem"
            source="isSystem"
            label={translate('resources.fee-templates.list.filters.isSystem')}
            choices={[
                { id: true, name: translate('resources.fee-templates.list.filters.systemTemplates') },
                { id: false, name: translate('resources.fee-templates.list.filters.customTemplates') },
            ]}
            emptyText={translate('resources.fee-templates.list.filters.all')}
            emptyValue=""
        />,
    ];
};

const TemplateStatusField = ({ record }: { record: FeeTemplate }) => {
    const translate = useTranslate();

    if (record.isSystem) {
        return (
            <Tooltip title={translate('resources.fee-templates.fields.systemTemplate')}>
                <Chip
                    icon={<AdminPanelSettings />}
                    label="System"
                    size="small"
                    color="primary"
                    variant="outlined"
                />
            </Tooltip>
        );
    }

    return (
        <Tooltip title={translate('resources.fee-templates.fields.customTemplate')}>
            <Chip
                icon={<PersonOutline />}
                label="Custom"
                size="small"
                color="default"
                variant="outlined"
            />
        </Tooltip>
    );
};

const UsageCountField = ({ record }: { record: FeeTemplate }) => {
    const translate = useTranslate();
    const count = record.usageCount || 0;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Typography variant="body2" color={count > 0 ? 'primary' : 'text.secondary'}>
                {count}
            </Typography>
            <Typography variant="caption" color="text.secondary">
                {translate(count === 1 ? 'resources.fee-templates.fields.feeSchedule' : 'resources.fee-templates.fields.feeSchedules')}
            </Typography>
        </Box>
    );
};

const CurrencyField = ({ record }: { record: FeeTemplate }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <AttachMoney fontSize="small" color="action" />
        <Typography variant="body2">{record.currency}</Typography>
    </Box>
);

const ComponentsField = ({ record }: { record: FeeTemplate }) => {
    const components = record.feeStructure?.components || {};
    const enabledComponents = Object.entries(components)
        .filter(([_, component]: [string, any]) => component?.enabled)
        .map(([key, _]) => key);

    const componentLabels: Record<string, string> = {
        sessionFee: 'S',
        energyFee: 'E',
        timeFee: 'T',
        idleFee: 'I',
        reservationFee: 'R',
    };

    const fullLabels: Record<string, string> = {
        sessionFee: 'Session',
        energyFee: 'Energy',
        timeFee: 'Time',
        idleFee: 'Idle',
        reservationFee: 'Reservation',
    };

    return (
        <Box sx={{ display: 'flex', gap: 0.5, maxWidth: 150 }}>
            {enabledComponents.map((component) => (
                <Tooltip key={component} title={fullLabels[component] || component}>
                    <Chip
                        label={componentLabels[component] || component}
                        size="small"
                        variant="outlined"
                        color="primary"
                        sx={{ minWidth: 28 }}
                    />
                </Tooltip>
            ))}
        </Box>
    );
};

const MobileList = () => {
    const { data, isLoading } = useListContext();
    const navigate = useNavigate();
    const translate = useTranslate();
    if (isLoading || !data) return null;
    return (
        <Box>
            {data.map((record: any) => (
                <RecordContextProvider key={record.id} value={record}>
                    <Box onClick={() => navigate(`/fee-templates/${record.id}/show`)}
                        sx={{ py: 1.5, px: 2, borderBottom: '1px solid #F0F0F0', cursor: 'pointer' }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.5 }}>
                            <Typography sx={{ fontWeight: 600, fontSize: '0.875rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1, minWidth: 0 }}>
                                {record.name}
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, flexShrink: 0 }}>
                                {record.isActive ? (
                                    <CheckCircle sx={{ fontSize: 16, color: '#00C853' }} />
                                ) : (
                                    <Cancel sx={{ fontSize: 16, color: '#F44336' }} />
                                )}
                                <Chip
                                    label={record.isSystem ? 'System' : 'Custom'}
                                    size="small"
                                    color={record.isSystem ? 'primary' : 'default'}
                                    variant="outlined"
                                    sx={{ height: 22, fontSize: '0.65rem' }}
                                />
                            </Box>
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
                            <Typography variant="caption" color="text.secondary">
                                {record.currency}
                            </Typography>
                            <Box sx={{ display: 'flex', gap: 0.25 }}>
                                {(() => {
                                    const components = record.feeStructure?.components || {};
                                    const labels: Record<string, string> = { sessionFee: 'S', energyFee: 'E', timeFee: 'T', idleFee: 'I', reservationFee: 'R' };
                                    return Object.entries(components)
                                        .filter(([_, c]: [string, any]) => c?.enabled)
                                        .map(([key]) => (
                                            <Chip key={key} label={labels[key] || key} size="small" variant="outlined" color="primary"
                                                sx={{ height: 20, fontSize: '0.6rem', minWidth: 22 }} />
                                        ));
                                })()}
                            </Box>
                            <Typography variant="caption" color="text.secondary">
                                {record.usageCount || 0} {translate(record.usageCount === 1 ? 'resources.fee-templates.fields.feeSchedule' : 'resources.fee-templates.fields.feeSchedules')}
                            </Typography>
                        </Box>
                    </Box>
                </RecordContextProvider>
            ))}
        </Box>
    );
};

export const FeeTemplatesList = () => {
    const translate = useTranslate();
    const feeTemplateFilters = useFeeTemplateFilters();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const navigate = useNavigate();

    return (
        <Box sx={{ position: 'relative' }}>
            <List
                filters={feeTemplateFilters}
                actions={isMobile ? <MobileListActions /> : <DesktopListActions />}
                empty={isMobile ? false : undefined}
                sort={{ field: 'name', order: 'ASC' }}
                perPage={25}
                title={translate('resources.fee-templates.name')}
                sx={{
                    '& .RaList-content': {
                        backgroundColor: isMobile ? 'transparent' : '#FAFAFA',
                        overflow: 'auto',
                    },
                    ...(isMobile && {
                        '& .RaList-main': { margin: 0 },
                        '& .RaList-content': { backgroundColor: 'transparent !important', boxShadow: 'none !important', mt: 2 },
                        '& .RaList-actions': { backgroundColor: 'transparent !important', p: '0 !important', minHeight: 'auto !important' },
                        '& .RaListToolbar-root': { backgroundColor: 'transparent !important', flexDirection: 'row !important', flexWrap: 'wrap !important', alignItems: 'center !important', gap: '8px !important' },
                        '& .RaListToolbar-root > span': { display: 'none' },
                        '& .RaFilterButton-root, & .add-filter': { display: 'none !important' },
                        '& .RaListToolbar-root > form, & .RaFilterForm-root': { flex: '1 1 100% !important', width: '100% !important' },
                        '& .filter-field, & .RaFilterForm-filterFormInput': { width: '100% !important' },
                        '& .filter-field .MuiFormControl-root': { width: '100% !important' },
                        '& .filter-field .MuiInputBase-root': { width: '100% !important' },
                        '& .MuiToolbar-root': { backgroundColor: 'transparent !important', minHeight: 'auto !important' },
                    }),
                }}
            >
                {isMobile ? (
                    <MobileList />
                ) : (
                    <Datagrid
                        bulkActionButtons={false}
                        sx={{
                            '& .RaDatagrid-headerCell': {
                                backgroundColor: '#F5F5F5',
                                fontWeight: 'bold',
                            },
                            '& .RaDatagrid-row': {
                                '&:hover': {
                                    backgroundColor: '#F0F7FF',
                                },
                            },
                            '& .RaDatagrid-table': {
                                tableLayout: 'fixed',
                            },
                            '& .column-name': {
                                width: '18%',
                            },
                            '& .column-type': {
                                width: '9%',
                            },
                            '& .column-currency': {
                                width: '7%',
                            },
                            '& .column-components': {
                                width: '14%',
                            },
                            '& .column-usage': {
                                width: '9%',
                            },
                            '& .column-isActive': {
                                width: '7%',
                            },
                            '& .column-createdAt': {
                                width: '12%',
                            },
                            '& .column-updatedAt': {
                                width: '12%',
                            },
                            '& .column-actions': {
                                width: '12%',
                                textAlign: 'right',
                            },
                        }}
                    >
                        <FunctionField
                            source="name"
                            label={translate('resources.fee-templates.fields.name')}
                            render={(record: FeeTemplate) => (
                                <Typography variant="body2" noWrap sx={{ maxWidth: 200 }}>
                                    {record.name}
                                </Typography>
                            )}
                            sortable={true}
                        />
                        <FunctionField
                            source="type"
                            label={translate('resources.fee-templates.fields.type')}
                            render={(record: FeeTemplate) => <TemplateStatusField record={record} />}
                            sortable={false}
                        />
                        <FunctionField
                            source="currency"
                            label={translate('resources.fee-templates.fields.currency')}
                            render={(record: FeeTemplate) => <CurrencyField record={record} />}
                            sortable={false}
                        />
                        <FunctionField
                            source="components"
                            label={translate('resources.fee-templates.fields.components')}
                            render={(record: FeeTemplate) => <ComponentsField record={record} />}
                            sortable={false}
                        />
                        <FunctionField
                            source="usage"
                            label={translate('resources.fee-templates.fields.usage')}
                            render={(record: FeeTemplate) => <UsageCountField record={record} />}
                            sortable={false}
                        />
                        <BooleanField
                            source="isActive"
                            label={translate('resources.fee-templates.fields.active')}
                            TrueIcon={CheckCircle}
                            FalseIcon={Cancel}
                            sortable={true}
                        />
                        <DateField
                            source="createdAt"
                            label={translate('resources.fee-templates.fields.createdAt')}
                            showTime={false}
                            sortable={true}
                        />
                        <DateField
                            source="updatedAt"
                            label={translate('resources.fee-templates.fields.updatedAt')}
                            showTime={false}
                            sortable={true}
                        />
                        <FunctionField
                            source="actions"
                            label=""
                            render={(record: FeeTemplate) => (
                                <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'flex-end' }}>
                                    <ShowButton size="small" record={record} label="" />
                                    <EditButton size="small" record={record} label="" />
                                    <DeleteButton size="small" record={record} label="" />
                                </Box>
                            )}
                        />
                    </Datagrid>
                )}
            </List>
            {isMobile && (
                <Fab color="primary" onClick={() => navigate('/fee-templates/create')}
                    sx={{ position: 'fixed', bottom: 24, right: 24, backgroundColor: '#009A75', '&:hover': { backgroundColor: '#007A5E' }, zIndex: 1100 }}>
                    <AddIcon />
                </Fab>
            )}
        </Box>
    );
};
